import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, Medal, Crown, ListChecks } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Player Tournament History — chronological list (newest first) of every
 * tournament the player has participated in, with their final position.
 *
 * Position is sourced from `placements` first (winner, runner_up, etc.) and
 * derived from match data otherwise (round_16, group_stage, etc.). Display
 * is read-only — admins manage placements via the tournament's bracket flow.
 */
const POSITION_STYLES = {
  winner: { label: "Champion", icon: Crown, badge: "bg-yellow-500/20 text-yellow-300 border-yellow-500/40" },
  runner_up: { label: "Runner-Up", icon: Trophy, badge: "bg-zinc-300/15 text-zinc-200 border-zinc-300/30" },
  semi_final: { label: "Semi-Finalist", icon: Medal, badge: "bg-orange-500/15 text-orange-300 border-orange-500/30" },
  quarter_final: { label: "Quarter-Finalist", icon: Medal, badge: "bg-amber-500/10 text-amber-300 border-amber-500/30" },
  third_place: { label: "Third Place", icon: Medal, badge: "bg-amber-600/15 text-amber-300 border-amber-600/30" },
  round_16: { label: "Round of 16", icon: ListChecks, badge: "bg-muted text-muted-foreground border-border" },
  round_32: { label: "Round of 32", icon: ListChecks, badge: "bg-muted text-muted-foreground border-border" },
  round_64: { label: "Round of 64", icon: ListChecks, badge: "bg-muted text-muted-foreground border-border" },
  group_stage: { label: "Group Stage", icon: ListChecks, badge: "bg-muted text-muted-foreground border-border" },
};

function PositionBadge({ position, label }) {
  const meta = POSITION_STYLES[position] || { icon: ListChecks, badge: "bg-muted text-muted-foreground border-border" };
  const Icon = meta.icon;
  return (
    <Badge
      variant="outline"
      className={`${meta.badge} text-[11px] font-medium gap-1.5 px-2 py-0.5`}
      data-testid={`position-badge-${position}`}
    >
      <Icon className="w-3 h-3" weight="fill" />
      {label || meta.label}
    </Badge>
  );
}

export default function PlayerTournamentHistory({ playerId }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!playerId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/players/${playerId}/tournament-history`)
      .then((r) => { if (!cancelled) setHistory(r.data?.tournaments || []); })
      .catch(() => { if (!cancelled) setHistory([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [playerId]);

  if (loading || history.length === 0) {
    return null;
  }

  return (
    <Card className="bg-card border-border" data-testid="tournament-history-card">
      <CardHeader>
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Trophy className="w-5 h-5 text-secondary" weight="fill" />
          Tournament History ({history.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {history.map((t, idx) => (
            <motion.div
              key={t.tournament_id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(idx * 0.02, 0.3) }}
              className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-background/40 hover:border-secondary/30 transition-colors"
              data-testid={`tournament-history-row-${t.tournament_id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{t.tournament_name}</p>
                <p className="text-[11px] text-muted-foreground">
                  {t.tournament_date}
                  {t.tournament_format && (
                    <> · <span className="capitalize">{t.tournament_format.replace(/_/g, " ")}</span></>
                  )}
                  {t.points_earned > 0 && (
                    <> · <span className="text-secondary">+{t.points_earned} pts</span></>
                  )}
                </p>
              </div>
              <PositionBadge position={t.position} label={t.position_label} />
            </motion.div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
